import { styled } from '@linaria/react';
import { Link } from 'react-router';
import { type CapabilityBucket, CapabilitySectionKind } from '../../../../../api/agents-client/agent-client.type';
import { Badge } from '../../../../../ui-library/badge/badge';
import { Icon } from '../../../../../ui-library/icon/icon';
import { CapabilityBucketCard } from '../../agent-customize/capability-bucket';
import { AgentIntegrationsView } from '../../agent-integrations/agent-integrations-view';
import { SaveStatus } from '../../shared/settings/save-status';
import { SettingsCard } from '../../shared/settings/settings-card';
import { FLORA_SETTINGS_PATHS } from '../agent-settings.constants';
import { FloraConnectionId } from '../agent-settings.type';
import { ConnectionTile } from '../connection-tile';
import { useFloraConnections } from '../use-flora-connections';
import { useFloraToolSettings } from '../use-flora-tool-settings';
import { Stack } from './settings-section-shell';

/** Connections whose connect / disconnect controls live in the integrations panels. */
const PANEL_CONNECTIONS: readonly string[] = [
  FloraConnectionId.Google,
  FloraConnectionId.Slack,
  FloraConnectionId.Teams,
];

interface SettingsConnectionDetailProps {
  /** The connection id from the URL. */
  appId: string;
}

/**
 * Renders one connection's settings page.
 *
 * Responsibilities:
 * 1. Show the connection's name and status, with a way back to Connections
 * 2. Reuse the integrations panel for Google, Slack, and Teams (connect,
 *    disconnect, admin uninstall, and what Flora can access)
 * 3. Show the catalog bucket's capabilities with their on/off switches once
 *    connected (autosaved)
 * 4. Say so when the id names no connection this organization has
 *
 * @param {SettingsConnectionDetailProps} props - The connection to show
 * @returns {JSX.Element} The connection page
 */
export function SettingsConnectionDetail({ appId }: SettingsConnectionDetailProps) {
  const { items, isLoading: isListLoading, isConnectionError } = useFloraConnections();
  const { capabilities, draft, error: toolSettingsError, saveState, handlers } = useFloraToolSettings();
  const item = items.find((connection) => connection.id === appId);
  const bucket = capabilities?.sections.find(
    (section): section is CapabilityBucket => section.kind === CapabilitySectionKind.Bucket && section.id === appId,
  );
  const hasPanel = PANEL_CONNECTIONS.includes(appId);
  // Slack and Teams have no catalog tools, so a failed catalog or preferences
  // load only hides the tool switches of the other connections.
  const isWorkspaceApp = appId === FloraConnectionId.Slack || appId === FloraConnectionId.Teams;
  const toolsFailed = Boolean(toolSettingsError) && !isWorkspaceApp && (!capabilities || bucket?.connected === true);

  return (
    <div>
      <Breadcrumb aria-label="Breadcrumb">
        <BreadcrumbLink to={FLORA_SETTINGS_PATHS.connections}>Connections</BreadcrumbLink>
        <IconSlot aria-hidden="true">
          <Icon name="chevron_right" size={16} color="var(--ink-light)" />
        </IconSlot>
        <span aria-current="page">{item?.name ?? appId}</span>
      </Breadcrumb>

      {isConnectionError(appId) ? (
        <SettingsCard role="alert">
          <Message>Failed to load connections.</Message>
        </SettingsCard>
      ) : !isListLoading && !item ? (
        <SettingsCard>
          <Message>
            This connection isn't available for your organization.{' '}
            <Link to={FLORA_SETTINGS_PATHS.connections}>Back to Connections</Link>
          </Message>
        </SettingsCard>
      ) : (
        <>
          <Header>
            <ConnectionTile connectionId={appId} />
            <HeaderText>
              <Title>{item?.name ?? ''}</Title>
              {item &&
                (item.connected ? (
                  <Badge tone="success" icon="check_circle">
                    Connected
                  </Badge>
                ) : (
                  <Badge tone="neutral">Not connected</Badge>
                ))}
            </HeaderText>
            {bucket?.connected && <SaveStatus state={saveState} />}
          </Header>

          <Stack>
            {hasPanel && (
              <SettingsCard>
                <PanelBody>
                  <AgentIntegrationsView embedded only={appId as FloraConnectionId} />
                </PanelBody>
              </SettingsCard>
            )}

            {bucket && !bucket.connected && !hasPanel && (
              <SettingsCard>
                <Message>Connect your {bucket.name} account to use these capabilities.</Message>
              </SettingsCard>
            )}

            {toolsFailed && (
              <SettingsCard role="alert">
                <Message>Failed to load this connection's tools.</Message>
              </SettingsCard>
            )}

            {bucket?.connected && draft && (
              <CapabilityBucketCard bucket={bucket} enabledTools={draft.enabledTools} handlers={handlers} />
            )}
          </Stack>
        </>
      )}
    </div>
  );
}

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  font-size: var(--font-size-small);
  color: var(--ink-dark);
`;

const BreadcrumbLink = styled(Link)`
  color: var(--secondary-darkest);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const HeaderText = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 240px;
  flex-wrap: wrap;
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: var(--font-size-large);
  line-height: var(--line-height-xlarge);
  font-weight: var(--font-weight-semibold);
  color: var(--ink-darkest);
`;

const PanelBody = styled.div`
  padding: 20px 24px 12px;
`;

const Message = styled.p`
  margin: 0;
  padding: 20px 24px;
  font-size: var(--font-size-small);
  color: var(--ink-base);
`;

export type { SettingsConnectionDetailProps };
